import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import './AdminLogin.css';
import { logoImage } from '../../assets/images';

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (!result?.success) {
        setError(result?.error || 'Invalid email or password');
        return;
      }
      navigate('/ft-portal/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { setForgotMsg('Please enter your email address.'); return; }
    setForgotLoading(true);
    setForgotMsg('');
    try {
      await api.post('/auth/admin-forgot-password', { email: forgotEmail.trim() });
      setForgotMsg('If an account exists with that email, a reset link has been sent. Check your inbox and spam folder.');
    } catch (err) {
      setForgotMsg(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="al-page">
      {/* Top bar */}
      {/* <div className="al-topbar">
        <div className="al-topbar-inner">
          <span>🇬🇧 DVSA Approved Driving School</span>
          <span>admin.fasttrackds.co.uk</span>
        </div>
      </div> */}

      {/* Body */}
      <div className="al-body">
        {/* Left info panel */}
        <div className="al-info">
          <img src={logoImage} alt="Fast Track Driving School" className="al-logo" />
          <h2 className="al-info-title">Admin Portal</h2>
          <p className="al-info-sub">Manage instructors, students and applications from one place.</p>
          <ul className="al-features">
            <li>✓ Instructor document verification</li>
            <li>✓ Student request management</li>
            <li>✓ Application review &amp; approval</li>
            <li>✓ Revenue &amp; referral tracking</li>
            <li>✓ Full audit log</li>
          </ul>
        </div>

        {/* Card */}
        <div className="al-card">
          <div className="al-card-accent" />
          <h1 className="al-card-title">{showForgot ? 'Reset Password' : 'Sign In'}</h1>
          <p className="al-card-sub">Fast Track Admin Portal</p>

          {!showForgot ? (
          <form onSubmit={handleSubmit} className="al-form">
            {error && (
              <div className="al-error">
                <span>⚠️</span> {error}
              </div>
            )}

            <div className="al-field">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@fasttrackds.co.uk"
                required
                autoComplete="username"
                disabled={loading}
              />
            </div>

            <div className="al-field">
              <label htmlFor="password">Password</label>
              <div className="al-pw-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="al-eye"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <button type="submit" className="al-submit" disabled={loading}>
              {loading ? <><span className="al-spinner" /> Signing In…</> : 'Sign In →'}
            </button>

            <p className="al-forgot-link">
              <button type="button" className="al-link" onClick={() => { setShowForgot(true); setError(''); }}>
                Forgot password?
              </button>
            </p>
          </form>
          ) : (
          <form onSubmit={handleForgotPassword} className="al-form">
            {forgotMsg ? (
              <div className={`al-forgot-msg ${forgotMsg.startsWith('If') ? 'al-forgot-msg--ok' : 'al-forgot-msg--err'}`}>
                {forgotMsg}
              </div>
            ) : null}

            {!forgotMsg && (
              <div className="al-field">
                <label htmlFor="forgot-email">Email Address</label>
                <input
                  type="email"
                  id="forgot-email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  placeholder="admin@fasttrackds.co.uk"
                  required
                  autoFocus
                />
              </div>
            )}

            {!forgotMsg && (
              <button type="submit" className="al-submit" disabled={forgotLoading}>
                {forgotLoading ? <><span className="al-spinner" /> Sending…</> : 'Send Reset Link →'}
              </button>
            )}

            <p className="al-forgot-link">
              <button type="button" className="al-link" onClick={() => { setShowForgot(false); setForgotMsg(''); setForgotEmail(''); }}>
                ← Back to Sign In
              </button>
            </p>
          </form>
          )}

          <p className="al-footer">🔒 Secure portal — all actions are logged</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
